// @ts-nocheck
import { OmniStyle, OmniIcon, html, css, nothing } from 'omni-ui';
import { OmniElementNext } from 'omni-ui/omni-element-next.js';
import { OmniAppContainerMixin } from 'omni-app-container';
import { api } from './helpers/api.js';
import { showLoader, hasRole } from './helpers/util.js';
import './module-modal.js';
import './helpers/image-modal.js';
import { om2confirm, om2alert } from 'omni-campaign-ui';

OmniStyle.register();
OmniIcon.register();

export default class ModuleList extends OmniAppContainerMixin(OmniElementNext) {
  static get properties() {
    return {
      tab: { type: Array },
      appTab: this.routeParamProperty({ name: 'appTab', source: 'tab' }),
      moduleId: this.routeParamProperty({ name: 'moduleId' }),
      stepId: this.routeParamProperty({ name: 'stepId' }),
      stageId: this.routeParamProperty({ name: 'stageId' }),
      workflowId: this.routeParamProperty({ name: 'workflowId' }),
      templateNames: { type: Array },
      modalInfo: { attribute: false },
      paginatedData: { type: Object },
      sort: { type: Object },
      search: { type: String },
      filters: { type: Array },
    };
  }

  constructor() {
    super();
    this.tabs = ['All Modules', 'Dynamic', 'Standard'];
    [this.tab] = this.tabs;
    this.sort = { key: 'name', dir: 'asc' };
    this.reloadId = 0;
    this.resetData();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  updated(changedProperties) {
    // Detect when the router gives us router param updates. All router params should be
    // set together, so checking for "appTab" changes will trigger this when the route
    // is "/module" and changes to moduleId will also occur when the route is
    // "/workflow/:workflowId/:stageId/:stepId/module/:moduleId"
    // In both cases we reset state and load from backend.
    if (
      (changedProperties.has('appTab') ||
        changedProperties.has('moduleId') ||
        changedProperties.has('tab')) &&
      (this.appTab || this.moduleId)
    ) {
      this.reload();
    }
  }

  /**
   * Manage Module styles
   */
  static get styles() {
    return [
      OmniElementNext.styles,
      css`
        ::part(module-type-img) {
          border-radius: 1rem;
        }
        omni-pagination {
          justify-items: right;
        }
      `,
    ];
  }

  resetData(page, perPage) {
    this.paginatedData = {
      page: page ?? this.paginatedData?.page ?? 1,
      per_page: perPage ?? this.paginatedData?.per_page ?? 10,
      total: 0,
      items: [],
    };
  }

  get breadcrumb() {
    const label = this.workflowId ? this.data?.[0]?.name : 'Module Library';
    const link = this.workflowId
      ? `workflow/${this.workflowId}/${this.stageId}/${this.stepId}/module/${this.moduleId}`
      : 'module';
    const toolbarActions = this.workflowId
      ? {}
      : { Add: () => this.addModule() };

    return { label, link, toolbarActions };
  }

  addModule() {
    this.openModulesModal('Add', null, false);
  }

  async editModule(moduleId) {
    const module = await showLoader(this, api.getModule(moduleId));
    this.openModulesModal('Edit', module, true);
  }

  async cloneModule(moduleId) {
    const module = await showLoader(this, api.getModule(moduleId));
    module.name = `${module.name} [cloned]`;
    delete module.uuid_module;
    this.openModulesModal('Clone', module, true);
  }

  async reload(setToFirstPage = true) {
    try {
      if (this.moduleId) {
        this.paginatedData = {
          ...this.paginatedData,
          items: [await showLoader(this, api.getModule(this.moduleId))],
        };
        this.dispatchNewEvent('breadcrumb-refresh');
      } else {
        // Bookkeeping to track the latest reload request and the current one
        this.reloadId += 1;
        const currentId = this.reloadId;

        if (setToFirstPage) this.paginatedData.page = 1;

        // Create filters (if any)
        let filters = [];
        if (this.tab === 'Dynamic') filters = ['module_type=form'];
        else if (this.tab === 'Standard') filters = ['module_type!=form'];

        const response = await showLoader(
          this,
          api.listModules({
            page: this.paginatedData.page,
            per_page: this.paginatedData.per_page,
            order: this.sort.key,
            order_direction: this.sort.dir,
            search: this.search,
            filters,
          })
        );

        // Ignore response if a later reload was performed. This avoids stale results from
        // a slow fetch call overwriting up-to-date results that returned earlier.
        if (currentId === this.reloadId) this.paginatedData = response;
      }
    } catch {
      om2alert(`Loading module data failed`, {
        type: 'danger',
        toast: true,
        closeTimeout: 5000,
      });
    }
  }

  get data() {
    return this.paginatedData.items;
  }

  openModulesModal(title, module, locked) {
    this.modalInfo = { title, module, locked };
  }

  closeModulesModal() {
    this.modalInfo = undefined;
  }

  async openChildRoute(name, module) {
    let url = `module/${module.uuid_module}/${name}`;
    if (this.workflowId) {
      url = `workflow/${this.workflowId}/${this.stageId}/${this.stepId}/module/${module.uuid_module}/${name}`;
    }
    this.navigateTo(url);
  }

  openFilteredWorkflows(module) {
    const url = `module/${module.uuid_module}/workflows`;
    this.navigateTo(url);
  }

  renderHeader() {
    if (this.moduleId) return nothing;

    const header = this.tabs.map(
      tab => html`
        <button
          slot="header-start"
          class="tab ${this.tab === tab ? 'active' : ''}"
          @click=${() => {
            this.tab = tab;
          }}
        >
          ${tab}
        </button>
      `
    );

    header.push(html`
      <omni-search
        slot="header-end"
        placeholder="Search Modules"
        @change=${e => {
          this.search = e.detail;
          this.reload();
        }}
      >
      </omni-search>
    `);
    return header;
  }

  renderPagination() {
    // Avoid setting totalItems to 0 which triggers omni-pagination to reset to page 1
    if (!this.paginatedData.total) return nothing;

    return html`
      <omni-pagination
        class="p-1 mt-1 mb-2"
        .totalItems=${this.paginatedData.total}
        .page=${this.paginatedData.page}
        .itemsPerPage=${this.paginatedData.per_page}
        @page-change=${e => {
          const { page, itemsPerPage } = e.detail;
          if (
            page !== this.paginatedData.page ||
            itemsPerPage !== this.paginatedData.per_page
          ) {
            this.resetData(page, itemsPerPage);
            this.reload(false);
          }
        }}
      >
      </omni-pagination>
    `;
  }

  deleteModule = async module => {
    try {
      await api.deleteModule(module.uuid_module);

      om2alert(`Module successfully deleted`, {
        type: 'success',
        toast: true,
        closeTimeout: 5000,
      });
      this.reload(false);
    } catch (e) {
      if (e.json.status === 409) {
        om2alert(
          `Module ${module.name} is currently in use on steps and cannot be deleted.`,
          { type: 'danger' }
        );
      } else {
        om2alert(`An error occurred: ${e.message}`, { type: 'danger' });
      }
    }
  };

  renderModules() {
    return html`
      ${this.modalInfo
        ? html` <module-modal
            .modalTitle=${this.modalInfo.title}
            .savedData=${this.modalInfo.module}
            .isModuleLocked=${this.modalInfo.locked}
            .closeModal=${() => this.closeModulesModal()}
            @module-updated=${() => this.reload(false)}
            @module-created=${e =>
              e.detail.module.module_type === 'form'
                ? this.openChildRoute('builder', e.detail.module)
                : this.reload()}
          ></module-modal>`
        : ''}

      <omni-style>
        <om2-table
          autotooltip
          shadowed
          .sort=${this.sort}
          @sort=${e => {
            this.sort = e.detail;
            this.reload();
          }}
          .columns=${[
            {
              label: 'name',
              key: 'name',
              isSortable: true,
              isMain: true,
            },
            {
              label: 'id',
              key: 'uuid_module',
              isSortable: true,
            },
            {
              label: 'description',
              key: 'description',
              isSortable: true,
            },
            {
              label: 'type',
              key: 'module_type',
              isSortable: true,
              template: type =>
                html`<td class="is-clamped">
                  <span>
                    ${this.templateNames?.find(t => t.id === type)?.name ??
                    type}
                  </span>
                </td>`,
            },
            {
              label: 'Module Template Image',
              key: 'module_template',
              template: moduleTemplate => {
                if (!moduleTemplate?.image_url) return html`<td>n/a</td>`;
                return html`
                  <td
                    part="table-body-cell table-body-module-template"
                    class="has-text-centered is-clamped"
                  >
                    <image-modal
                      imageURL=${moduleTemplate?.image_url}
                    ></image-modal>
                  </td>
                `;
              },
              isSortable: false,
            },
            {
              label: 'actions',
              passthrough: true,
              template: module => html`
                <td>
                  <button
                    @click=${() => this.editModule(module.uuid_module)}
                    class="icon"
                    title="Edit"
                  >
                    <omni-icon icon-id="omni:interactive:edit"></omni-icon>
                  </button>
                  <button
                    @click="${() => this.cloneModule(module.uuid_module)}"
                    class="icon ml-3 ${this.moduleId ? 'is-hidden' : ''}"
                    title="Clone"
                  >
                    <omni-icon icon-id="omni:interactive:copy"></omni-icon>
                  </button>
                  ${module.module_type === 'form'
                    ? html`
                        <button
                          @click="${() => {
                            this.openChildRoute('builder', module);
                          }}"
                          class="icon ml-3"
                          title="Form Builder"
                        >
                          <omni-icon
                            icon-id="omni:interactive:build"
                          ></omni-icon>
                        </button>
                      `
                    : nothing}
                  ${module.module_template?.assist_fields?.length ||
                  module.module_type === 'form'
                    ? html`<button
                        @click="${() => this.openChildRoute('assist', module)}"
                        class="icon ml-3"
                        title="Omni Assist"
                      >
                        <omni-icon icon-id="omni:app:assistSm"></omni-icon>
                      </button>`
                    : nothing}
                  <button
                    @click="${() => this.openFilteredWorkflows(module)}"
                    class="icon ml-3"
                    title="Workflows"
                  >
                    <omni-icon icon-id="omni:object:workflow"></omni-icon>
                  </button>
                  ${hasRole('super admin')
                    ? html`
                        <button
                          @click="${async () => {
                            const isModuleInFramework =
                              await api.listFrameworkTemplates({
                                moduleId: module.uuid_module,
                                fields: ['uuid_framework_template'],
                              });
                            if (isModuleInFramework.length > 0) {
                              return om2confirm(
                                html`<p>
                                  This module, ${module.name}, is currently
                                  being used in a step and cannot be deleted.
                                  Refer to the workflows icon (
                                  <omni-icon
                                    class="is-size-2"
                                    icon-id="omni:object:workflow"
                                    style="vertical-align: middle; display: inline-block;"
                                  ></omni-icon>
                                  ) to see where it's being used.
                                </p>`,
                                {
                                  type: 'warning',
                                }
                              );
                            }
                            om2confirm(
                              'Are you sure you want to delete this module? This action is permanent and cannot be undone.',
                              {
                                callback: async () =>
                                  await this.deleteModule(module),
                                type: 'warning',
                              }
                            );
                          }}"
                          class="icon ml-3"
                          title="Delete"
                          ?disabled=${module.active}
                        >
                          <omni-icon
                            icon-id="omni:interactive:delete"
                          ></omni-icon>
                        </button>
                      `
                    : nothing}
                </td>
              `,
            },
          ]}
          .data="${this.data}"
        >
          ${this.renderHeader()}
        </om2-table>
        ${this.moduleId ? nothing : this.renderPagination()}
      </omni-style>
    `;
  }

  render() {
    return html`<slot>${this.renderModules()}</slot>`;
  }
}

customElements.define('module-list', ModuleList);
